// StructureDrawer — panel lateral "Análisis de Estructura de Mercado".
//
// DECISIONES DE DISEÑO (no reabrir):
//  · No predice — muestra INGREDIENTES (Rango activo, eventos, contexto).
//  · No usa "Wyckoff" como tag de fase con falsa precisión — eventos llevan
//    `interpretation` ('posible_spring', etc.) + `conviction` tier.
//  · Disclaimer fijo arriba, visible sin hover, en 3 idiomas (ES/EN/SK).
//  · Patrón visual idéntico al CascadeRecentDrawer (consistencia UX).
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { captureChartAsBlob, tryNativeShare } from "../../lib/captureChart";
import rebelWatermarkUrl from "../../assets/branding/rebel_watermark.png";
import { useLanguage } from "../../i18n/LanguageContext";
import { tLm } from "../../i18n/liqmap";

const DISCLAIMERS = {
  es: "Wyckoff es interpretivo. La identificación de estructuras en tiempo real tiene alta incertidumbre — use como contexto, no como señal de entrada.",
  en: "Wyckoff analysis is interpretive. Real-time structure identification carries high uncertainty — use as context, not as an entry signal.",
  sk: "Wyckoffova analýza je interpretačná. Identifikácia štruktúr v reálnom čase má vysokú neistotu — používajte ako kontext, nie ako vstupný signál.", de: "Wyckoff ist interpretativ. Die Identifizierung von Strukturen in Echtzeit hat hohe Unsicherheit — als Kontext verwenden, nicht als Einstiegssignal.", "zh-CN": "Wyckoff 是解释性的。实时识别结构具有高度不确定性——应作为背景参考，而非入场信号。",
};

const fmtUsd = (n) => {
  if (n == null || isNaN(n)) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const fmtPct = (n) => (n == null || isNaN(n) ? "—" : `${(n * 100).toFixed(2)}%`);

// Duration tiers — Wyckoff wisdom: "una consolidación larga vale más que una corta".
// Standard (<7d): verde normal · Extended (≥7d): dorado · Major (≥14d): dorado pulsante.
const durationTier = (hours) => {
  if (hours == null) return null;
  if (hours >= 336) return "major";      // ≥14 días
  if (hours >= 168) return "extended";   // ≥7 días
  return "standard";
};

const ConvictionDot = ({ tier }) => {
  const color = tier === "high" ? "#00d084"
              : tier === "medium" ? "#f97316"
              : tier === "low" ? "#9ca3af"
              : "#4b5563";
  return (
    <span
      data-testid="wyckoff-conviction-dot"
      data-tier={tier}
      className="inline-block h-2 w-2 rounded-full"
      style={{ background: color, boxShadow: `0 0 4px ${color}` }}
    />
  );
};

const interpretationLabel = (kind, t) => {
  switch (kind) {
    case "spring": return t.spring_label;
    case "utad":   return t.utad_label;
    case "sc":     return t.sc_label;
    case "bc":     return t.bc_label;
    default:       return kind;
  }
};

export default function StructureDrawer({ open, onClose, coin, tf = "1h", token, apiBase, onShareToShadow }) {
  const { lang } = useLanguage();
  const t = tLm(lang).wyckoff || tLm("es").wyckoff;
  const disclaimer = DISCLAIMERS[lang] || DISCLAIMERS.en || DISCLAIMERS.es;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTf, setActiveTf] = useState(tf);

  useEffect(() => { setActiveTf(tf); }, [tf, open]);

  useEffect(() => {
    if (!open || !coin) return;
    let aborted = false;
    setLoading(true);
    setError(null);
    const url = `${apiBase}/api/liqmap/wyckoff/${coin}?tf=${activeTf}`;
    fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((j) => { if (!aborted) setData(j); })
      .catch((e) => { if (!aborted) setError(String(e)); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [open, coin, activeTf, apiBase, token]);

  // ── Share to Shadow ────────────────────────────────────────────────────
  // Captures the `wyckoff-share-root` region as a screenshot (disclaimer +
  // sections travel with the post — same "honesty travels" pattern as
  // Cycles Map). Description reuses the SAME disclaimer copy verbatim.
  const handleShare = async () => {
    if (!onShareToShadow) return;
    const target = typeof document !== "undefined"
      ? document.querySelector('[data-testid="wyckoff-share-root"]')
      : null;
    if (!target) {
      toast.error(t.share_capture_failed || "No se pudo capturar el análisis");
      return;
    }
    const blob = await captureChartAsBlob({ target, watermarkUrl: rebelWatermarkUrl });
    if (!blob) {
      toast.error(t.share_capture_failed || "No se pudo capturar el análisis");
      return;
    }
    // Build description with the SAME disclaimer verbatim — honesty travels.
    const lines = [`📐 ${t.title} · ${coin} · ${activeTf}`];
    const rng = data?.range;
    if (rng?.is_ranging) {
      lines.push(`${t.ranging_yes} — ${t.range_high}: ${rng.range_high} · ${t.range_low}: ${rng.range_low}`);
      lines.push(`${t.confidence}: ${(rng.confidence * 100).toFixed(0)}%`);
    } else {
      lines.push(t.ranging_no);
    }
    const evs = (data?.recent_events || []);
    if (evs.length) {
      lines.push("");
      lines.push(`⚠ ${evs.length} ${t.events_section_title.toLowerCase()}`);
      evs.slice(0, 3).forEach((e) => {
        lines.push(`  • ${interpretationLabel(e.kind, t)} — ${t[`conviction_${e.conviction}`] || e.conviction}`);
      });
    }
    lines.push("");
    lines.push(`ℹ️ ${disclaimer}`);
    onShareToShadow({
      image: blob,
      title: "",
      description: lines.join("\n"),
      ticker: coin,
      timeframe: activeTf,
      direction: "",
    });
    tryNativeShare(blob, { title: `${coin} · ${t.title}` });
  };

  if (!open) return null;

  const r = data?.range;
  const events = data?.recent_events || [];
  const cluster = data?.cluster_context || {};

  return (
    <>
      <div
        data-testid="wyckoff-drawer-backdrop"
        onClick={onClose}
        className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
      />
      <aside
        data-testid="wyckoff-drawer"
        className="fixed right-0 top-0 z-[81] h-full w-full max-w-[480px] overflow-y-auto border-l border-[#f97316]/20 bg-[#0a0a0c] p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              {t.title_overline}
            </h3>
            <h2 className="mt-1 font-mono text-lg font-black tracking-wide text-white">
              {t.title}
            </h2>
            <p className="mt-0.5 font-mono text-[10px] text-white/30">{coin} · {activeTf}</p>
          </div>
          <button
            type="button"
            data-testid="wyckoff-drawer-close"
            onClick={onClose}
            className="rounded-md border border-white/10 px-2 py-1 font-mono text-xs text-white/60 hover:bg-white/[0.04]"
          >
            ✕
          </button>
        </div>

        {/* Share-root wrapper: disclaimer + analytical sections travel
            together as the screenshot. Header / TF-switcher / loading /
            share-button stay OUTSIDE so they never leak into the shared
            image. */}
        <div data-testid="wyckoff-share-root">
          {/* Disclaimer — fijo, sin hover, requerido por diseño */}
          <div
            data-testid="wyckoff-disclaimer"
            className="mb-4 flex gap-2 rounded-md border border-[#f97316]/40 bg-[#f97316]/[0.08] p-3 font-mono text-[11px] leading-snug text-[#f97316]"
          >
            <span className="mt-0.5 shrink-0">⚠</span>
            <span>{disclaimer}</span>
          </div>

          {/* Analytical sections (rendered only when data loaded). */}
          {data && !loading && (
            <>
              {/* Rango activo */}
              <section
                data-testid="wyckoff-range-section"
                className="mb-4 rounded-md border border-white/[0.08] bg-white/[0.02] p-3"
              >
                <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                  {t.range_section_title}
                </h4>
                <div className="flex items-center gap-2">
                  <span
                    data-testid="wyckoff-range-status"
                    className="font-mono text-xs font-black"
                    style={{ color: r?.is_ranging ? "#00d084" : "#9ca3af" }}
                  >
                    {r?.is_ranging ? t.ranging_yes : t.ranging_no}
                  </span>
                  {r?.is_ranging && (
                    <span className="font-mono text-[10px] text-white/40">
                      {t.confidence}: {(r.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                {r?.is_ranging && (
                  <div className="mt-2 grid grid-cols-2 gap-2 font-mono text-[11px]">
                    <div><span className="text-white/40">{t.range_high}:</span> <span className="text-white">{fmtUsd(r.range_high)}</span></div>
                    <div><span className="text-white/40">{t.range_low}:</span> <span className="text-white">{fmtUsd(r.range_low)}</span></div>
                    <div><span className="text-white/40">{t.midpoint}:</span> <span className="text-white">{fmtUsd(r.midpoint)}</span></div>
                    <div><span className="text-white/40">{t.range_pct}:</span> <span className="text-white">{fmtPct(r.range_pct)}</span></div>
                    <div className="col-span-2"><span className="text-white/40">{t.window_label}:</span> <span className="text-white">{r.age_candles} {t.candles_unit}</span></div>
                    {r.duration_hours != null && (() => {
                      const tier = durationTier(r.duration_hours);
                      const tierColor = tier === "standard" ? "#00d084" : "#eab308";
                      const tierLabel = tier === "major"
                        ? t.duration_tier_major
                        : tier === "extended"
                          ? t.duration_tier_extended
                          : null;
                      const durStr = r.duration_hours >= 168
                        ? `${(r.duration_hours / 168).toFixed(1)}w`
                        : r.duration_hours >= 24
                          ? `${(r.duration_hours / 24).toFixed(1)}d`
                          : `${Math.round(r.duration_hours)}h`;
                      return (
                        <div
                          data-testid="wyckoff-duration-row"
                          data-tier={tier}
                          className="col-span-2"
                        >
                          <span className="text-white/40">{t.duration_label}:</span>{" "}
                          <span
                            className={"font-black " + (tier === "major" ? "animate-pulse" : "")}
                            style={{ color: tierColor }}
                          >
                            {durStr}
                          </span>{" "}
                          <span className="text-white/40">({r.duration_candles} {t.candles_unit})</span>
                          {tierLabel && (
                            <div
                              data-testid="wyckoff-duration-tier-label"
                              className={"mt-1 inline-block rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider " + (tier === "major" ? "animate-pulse" : "")}
                              style={{ color: tierColor, background: `${tierColor}22`, border: `1px solid ${tierColor}66` }}
                            >
                              ✧ {tierLabel}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                {!r?.is_ranging && (
                  <p className="mt-1 font-mono text-[10px] text-white/40">
                    {t.no_range_hint}
                  </p>
                )}
              </section>

              {/* Eventos recientes */}
              <section
                data-testid="wyckoff-events-section"
                className="mb-4 rounded-md border border-white/[0.08] bg-white/[0.02] p-3"
              >
                <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                  {t.events_section_title}
                </h4>
                {events.length === 0 && (
                  <p data-testid="wyckoff-events-empty" className="font-mono text-[11px] text-white/40">
                    {t.no_events}
                  </p>
                )}
                {events.map((e, i) => (
                  <div
                    key={i}
                    data-testid={`wyckoff-event-${e.kind}`}
                    className="mb-2 flex items-start gap-2 border-b border-white/[0.05] pb-2 last:border-0 last:pb-0"
                  >
                    <ConvictionDot tier={e.conviction} />
                    <div className="flex-1">
                      <p className="font-mono text-[11px] font-black text-white">
                        {interpretationLabel(e.kind, t)} <span className="text-white/40">·</span> <span className="text-white/60">{t[`conviction_${e.conviction}`] || e.conviction}</span>
                      </p>
                      <p className="font-mono text-[10px] text-white/50">
                        {new Date(e.candle_ts || e.ts).toLocaleString()}
                      </p>
                      {e.oi_delta_pct != null && (
                        <p className="font-mono text-[10px] text-white/40">
                          ΔOI: <span style={{ color: e.oi_delta_pct < 0 ? "#00d084" : "#ff3b5c" }}>{(e.oi_delta_pct * 100).toFixed(2)}%</span>
                          {e.vol_ratio != null && <> · vol: {e.vol_ratio.toFixed(2)}×</>}
                        </p>
                      )}
                      {e.cluster_price != null && (
                        <p className="font-mono text-[10px] text-white/40">
                          {t.cluster_at}: {fmtUsd(e.cluster_price)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </section>

              {/* Contexto de cluster */}
              {cluster.cluster_max_price && (
                <section
                  data-testid="wyckoff-cluster-section"
                  className="rounded-md border border-white/[0.08] bg-white/[0.02] p-3"
                >
                  <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                    {t.cluster_section_title}
                  </h4>
                  <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
                    <div><span className="text-white/40">{t.cluster_max}:</span> <span className="text-white">{fmtUsd(cluster.cluster_max_price)}</span></div>
                    <div><span className="text-white/40">{t.cluster_second}:</span> <span className="text-white">{fmtUsd(cluster.cluster_second_price)}</span></div>
                  </div>
                  <p className="mt-2 font-mono text-[10px] text-white/40">{t.cluster_hint}</p>
                </section>
              )}
            </>
          )}
        </div>{/* /wyckoff-share-root */}

        {/* TF switcher — OUTSIDE share-root */}
        <div className="mt-3 flex gap-1 rounded-md border border-white/5 bg-white/[0.02] p-1">
          {["15m", "1h", "4h", "1d"].map((x) => (
            <button
              key={x}
              type="button"
              data-testid={`wyckoff-tf-${x}`}
              onClick={() => setActiveTf(x)}
              className={
                "flex-1 rounded px-2 py-1 font-mono text-[10px] font-bold transition-all " +
                (activeTf === x
                  ? "bg-[#f97316]/20 text-[#f97316]"
                  : "text-white/40 hover:text-white/70")
              }
            >
              {x}
            </button>
          ))}
        </div>

        {loading && (
          <p data-testid="wyckoff-loading" className="mt-3 font-mono text-xs text-white/40">{t.loading}</p>
        )}
        {error && (
          <p data-testid="wyckoff-error" className="mt-3 font-mono text-xs text-[#ff3b5c]">{t.error}</p>
        )}

        {/* Share to Shadow — OUTSIDE share-root so it doesn't leak into
            the captured screenshot. */}
        {data && !loading && onShareToShadow && (
          <button
            type="button"
            data-testid="wyckoff-share-to-shadow"
            onClick={handleShare}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-[#a855f7]/40 bg-[#a855f7]/[0.10] px-3 py-2 font-mono text-[11px] font-bold text-[#a855f7] transition hover:bg-[#a855f7]/[0.18]"
          >
            🌘 {t.share_to_shadow}
          </button>
        )}
      </aside>
    </>
  );
}
